import { inject } from '@angular/core';
import { type WritableDraft } from 'immer';
import { first, type Observable, of, switchMap } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type AggregateFunctionName } from '@blaze-shared/api-contracts-domain';
import {
  type AggregateWorkflowNode,
  findFirstNodeInWorkflowOfType,
  isAggregateNode,
} from '@blaze-shared/workflow-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AiWorkflowGenerationService,
  type ElementHandler,
  findElement,
  getElement,
  patchTitle,
  upsertElement,
} from '../../../ai';
import { toWidthSettings } from '../../../ai/models/styles';

import { type ValueChartContext, type ValueChartResolvedContext } from './value-chart.definition';

export class ValueChartElementHandler implements ElementHandler<'chartSingle'> {
  private readonly aiWorkflowGenerationService = inject(AiWorkflowGenerationService);

  readonly type = 'chartSingle';

  prepare(context: ValueChartContext): Observable<ValueChartResolvedContext> {
    const workflowContext = (context as any).componentWorkflows[context.element.id];

    if (!workflowContext?.workflow) {
      return of(context);
    }

    return of({
      ...context,
      workflowContext,
    });
  }

  process(context: ValueChartResolvedContext, draft: WritableDraft<EngineState>): void {
    const props = this.getChartProps(context, draft);

    const result = upsertElement(draft, context, props);
    const element = getElement(draft, result.elementId, this.type);

    if (context.workflowContext?.workflow) {
      const { workflow } = context.workflowContext;

      // TODO: Filip - should be removed after fixing BE
      const aggregateNode = findFirstNodeInWorkflowOfType(
        workflow,
        (node): node is AggregateWorkflowNode => isAggregateNode(node) && !node.nextNodeId,
      );
      if (aggregateNode) {
        aggregateNode.functionDefs[0].alias = 'chartAggregation';
      }
      // TODO: Filip - end of todo

      draft.workflows.push(workflow);
    }

    if (context.element.width) {
      element.width = toWidthSettings(context.element.width);
    } else {
      element.width = {
        mode: 'full',
      };
    }

    if (context.element.height) {
      element.height = {
        mode: 'value',
        value: context.element.height,
        fixed: true,
      };
    }
  }

  finalize(
    element: CanvasElementFrom<'chartSingle'>,
    context: ValueChartResolvedContext,
  ): Observable<any> {
    if (!context.workflowContext) {
      return of(null);
    }

    const { workflow, inputVariables, resolvedLayoutInputs } = context.workflowContext;

    return this.aiWorkflowGenerationService.getWorkflowRelatedDetails(element.id).pipe(
      first(),
      switchMap(({ relatedDatasetMap, relatedDetails }) =>
        this.aiWorkflowGenerationService.finalizeAiWorkflowCreation(
          element.id,
          workflow,
          inputVariables,
          relatedDatasetMap,
          relatedDetails,
          'value',
          true, // TODO: Filip - should be removed after fixing BE
          undefined,
          resolvedLayoutInputs,
        ),
      ),
    );
  }

  private getInitialProps(
    context: ValueChartResolvedContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'chartSingle'> {
    const element = findElement<'chartSingle'>(draft, context.element.id);
    return (
      element?.props ?? {
        content: {
          chart: {
            showTotal: true,
          },
        },
      }
    );
  }

  private getChartProps(
    context: ValueChartResolvedContext,
    draft: WritableDraft<EngineState>,
  ): PartialPropsOf<'chartSingle'> {
    const data = context.element.data;

    let props = this.getInitialProps(context, draft);

    if (context.element.title) {
      props = patchTitle<'chartSingle'>(props, context.element.title, context.element.showTitle);
    }

    // if (context.element.description) {
    //   props = applyPropsPatch(props, {
    //     content: {
    //       title: {
    //         description: context.element.description,
    //       },
    //     },
    //   });
    // }

    if (context.element.format) {
      props = applyPropsPatch(props, {
        content: {
          format: {
            type: context.element.format,
          },
        },
      });
    }

    if (context.element.precision) {
      props = applyPropsPatch(props, {
        content: {
          format: {
            precision: context.element.precision,
          },
        },
      });
    }

    if (data?.$type) {
      props = applyPropsPatch(props, {
        content: {
          data: {
            aggregation: (data?.$type === 'value' ? 'FIELD VALUE' : data?.$type.toUpperCase()) as
              | AggregateFunctionName
              | 'FIELD VALUE',
          },
        },
      });
    }

    if (context.workflowContext) {
      props = applyPropsPatch(props, {
        data: [
          {
            key: 'value',
            dataSource: {
              id: context.workflowContext.workflow.id,
              name: '',
              sourceType: 'context',
              columns: [],
            },
            bindings: {},
            enabled: true,
            isCustomSelection: false,
          },
        ],
      });
    }

    return props;
  }
}
